import { Core } from "@faire/design-tokens";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization";
import { Calendar60 } from "@faire/web/slate/icons/CalendarDays";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";

import { useFaireDirectBrand } from "@faire/retailer/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { useFaireDirectFirstOrderIncentive } from "@faire/retailer/hooks/FaireDirect/useFaireDirectFirstOrderIncentive/useFaireDirectFirstOrderIncentive";
import CuratedSelectionIcon from "@faire/retailer/packages/core-icons/values/Collections.svg";
import FreeReturnsIcon from "@faire/retailer/packages/core-icons/values/FreeReturn.svg";
import { termsOfServiceLinkFormatting } from "@faire/retailer/views/product/Components/__internal__/utils";

const Wrapper = styled(Column).attrs({
  justify: "flex-start",
  align: "center",
})`
  width: 100%;
  background-color: ${Core.surface.muted};
  padding: 24px;
`;

const Title = styled(Typography).attrs({
  variant: "pageHeaderSerifRegular",
  align: "center",
})`
  &&& {
    font-size: 14px;
    line-height: 24px;
    letter-spacing: 0.25px;
  }
`;

const PerkRow = styled(Row).attrs({ align: "center", justify: "center" })`
  margin-bottom: 32px;
  width: 100%;
`;

const PerkTypography = styled(Typography).attrs({})``;

const Icon = styled(Column).attrs({ align: "center", justify: "center" })`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${Core.surface.inverse};
  margin-right: 20px;
`;

const DesktopFaireDirectPerksCardBase: React.FC = () => {
  const { faireDirectBrand } = useFaireDirectBrand();
  const { isFaireDirectCreditPercentageBased, formattedFixedBasedAmount } =
    useFaireDirectFirstOrderIncentive();

  if (!faireDirectBrand) {
    return null;
  }

  return (
    <Wrapper>
      <Typography align="center">
        <strong>
          <StrictLocalMsg
            defaultMessage="{brandName} invited you to Faire!"
            description={{
              text: "a brand invites a retailer to sign up on Faire",
            }}
            values={DEPRECATED_backwardsCompatibleValues({
              brandName: faireDirectBrand.name,
            })}
          />
        </strong>
      </Typography>
      {isFaireDirectCreditPercentageBased ? null : (
        <>
          <Spacer size={16} />
          <Title>
            <StrictLocalMsg
              defaultMessage="Sign up to lock in {promoMoney} off and one year of free shipping to {brandCompanyName}"
              description={{
                text: "a retailer signs up to lock in a monetary amount off to a brand",
              }}
              values={{
                promoMoney: formattedFixedBasedAmount,
                brandCompanyName: faireDirectBrand.name ?? "",
              }}
            />
          </Title>
        </>
      )}
      <Spacer size={32} />
      <PerkRow>
        <Icon>
          <img
            src={CuratedSelectionIcon}
            alt="Easy reordering with year-round shopping"
            height="20"
            width="20"
          />
        </Icon>
        <Column flex={1}>
          <PerkTypography>
            <StrictLocalMsg
              defaultMessage="Easy reordering with year-round shopping"
              description={{
                text: "One of the benefits Faire offers. Faire offers shipping and reorders on products and brands any time of the year.",
              }}
            />
          </PerkTypography>
        </Column>
      </PerkRow>
      <PerkRow>
        <Icon>
          <img
            src={FreeReturnsIcon}
            alt="Free returns for risk-free buying"
            height="17"
            width="20"
          />
        </Icon>
        <Column flex={1}>
          <PerkTypography>
            <StrictLocalMsg
              defaultMessage="Free returns for risk-free buying"
              description={{
                text: "One of the benefits Faire offers. Retailers get free returns on orders to reduce risk when buying new products.",
              }}
            />
          </PerkTypography>
        </Column>
      </PerkRow>
      <PerkRow>
        <Icon>
          <Calendar60
            height="20px"
            width="20px"
            titleAccess="60-day payment terms"
          />
        </Icon>
        <Column flex={1}>
          <PerkTypography>
            <StrictLocalMsg
              defaultMessage="60-day payment terms"
              description={{
                text: "For retailers | Desktop and moweb | Displays on logged-out product details page | Faire Direct banner | 1 of 3 value props | Preceded by this header: Get {promoMoney} off {brandCompanyName} | Placeholders in header show the amount a retailer can save on their first order with the brand.",
                img: "https://cdn.faire.com/fastly/5f269659cc0ae3c9256a13f92e73c737a7428dc059b334b8fc04859c8f0bdcb7.png",
              }}
            />
          </PerkTypography>
        </Column>
      </PerkRow>
      <Spacer size={8} />
      <Typography
        color={Core.text.subdued}
        align="center"
        variant="labelSansRegular"
      >
        <StrictLocalMsg
          defaultMessage="Eligible for new retailers only. By proceeding, you agree to our <TextLinkFormatting>Terms of Service</TextLinkFormatting>"
          values={{
            TextLinkFormatting: termsOfServiceLinkFormatting,
          }}
          description={{
            text: "Terms and privacy policy disclaimer message for proceeding in sign up process",
            img: "https://cdn.faire.com/fastly/88d45a9b5d2886575380320cc0d34d9f9adac4a307b86bc5521f86f7967864f2.png",
          }}
        />
      </Typography>
    </Wrapper>
  );
};

export const DesktopFaireDirectPerksCard = observer(
  DesktopFaireDirectPerksCardBase
);
